import React, { useState, useCallback } from 'react';
import { Col, Container, Form, Row, Alert } from 'reactstrap';
import axios from 'axios';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

/**
 * The contact form, used twice: as a section of the landing page and as the
 * body of /contact.
 *
 * `showHeading` is false on that page, which introduces the form with its own
 * words — without it the reader met "Get in touch" immediately followed by
 * "Get In Touch", which reads as a page that was assembled rather than written.
 */
const ContactForm = ({ showHeading = true }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });
    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState({ show: false, type: '', message: '' });
    
    // Bot protection
    const [honeypot, setHoneypot] = useState(''); // Hidden field - bots will fill this
    const [formLoadTime] = useState(Date.now()); // Track when form was loaded
    
    // reCAPTCHA v3
    const { executeRecaptcha } = useGoogleReCaptcha();

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        setLoading(true);
        setAlert({ show: false, type: '', message: '' });

        // Bot detection: honeypot field should be empty
        if (honeypot) {
            // Silently reject - don't tell bots they've been caught
            setAlert({
                show: true,
                type: 'success',
                message: 'Thank you for your message! We will get back to you soon.'
            });
            setLoading(false);
            return;
        }

        // Bot detection: form submitted too quickly (under 3 seconds)
        const timeSpent = Date.now() - formLoadTime;
        if (timeSpent < 3000) {
            setAlert({
                show: true,
                type: 'danger',
                message: 'Please take your time filling out the form.'
            });
            setLoading(false);
            return;
        }

        // Get reCAPTCHA v3 token
        if (!executeRecaptcha) {
            setAlert({
                show: true,
                type: 'danger',
                message: 'reCAPTCHA not ready. Please try again.'
            });
            setLoading(false);
            return;
        }

        try {
            // Execute reCAPTCHA v3 with action name
            const recaptchaToken = await executeRecaptcha('contact_form');
            
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
            
            const response = await axios.post(`${apiUrl}/api/contact`, {
                ...formData,
                recaptchaToken,
                _timeSpent: timeSpent
            });
            
            console.log('Response received:', response);

            // Note: axios interceptor returns response.data directly
            if (response && response.success) {
                setAlert({
                    show: true,
                    type: 'success',
                    message: response.message || 'Thank you for your message! We will get back to you soon.'
                });
                // Reset form
                setFormData({
                    name: '',
                    email: '',
                    subject: '',
                    message: ''
                });
            } else {
                // Response received but success flag is false or missing
                setAlert({
                    show: true,
                    type: 'danger',
                    message: response?.message || 'Failed to send message. Please try again later.'
                });
            }
        } catch (error) {
            console.error('Contact form error:', error);
            const errorMessage = error.response?.data?.message || error || 'Failed to send message. Please try again later.';
            setAlert({
                show: true,
                type: 'danger',
                message: errorMessage
            });
        } finally {
            setLoading(false);
        }
    }, [executeRecaptcha, formData, honeypot, formLoadTime]);

    return (
        <React.Fragment>
            {/* .section is 90px top and bottom. With the heading hidden
                that top padding is bare space under whatever introduced the
                form, so it is dropped in that case. */}
            <section className={`section ${showHeading ? '' : 'pt-0'}`} id="contact">
                <Container>
                    <Row className="justify-content-center">
                        {showHeading && (
                        <Col lg={8}>
                            <div className="text-center mb-5">
                                <h3 className="mb-3 fw-semibold">Get In Touch</h3>
                                {/* Was "our AI-powered crypto predictions ... our
                                    platform", which sells predictions and names
                                    crypto first. Neither is what this is. */}
                                <p className="text-muted mb-4 ff-secondary">Questions about the service, your account, a refund, or your data — this reaches us directly.</p>
                            </div>
                        </Col>
                        )}
                    </Row>

                    <Row className="gy-4">
                        <Col lg={12}>
                            {alert.show && (
                                <Alert color={alert.type} className="mb-4">
                                    {alert.message}
                                </Alert>
                            )}
                            <div>
                                <Form onSubmit={handleSubmit}>
                                    <Row>
                                        <Col lg={6}>
                                            <div className="mb-4">
                                                <label htmlFor="name" className="form-label fs-13">Name</label>
                                                <input 
                                                    name="name" 
                                                    id="name" 
                                                    type="text"
                                                    className="form-control bg-light border-light" 
                                                    placeholder="Your name*"
                                                    value={formData.name}
                                                    onChange={handleChange}
                                                    required
                                                    disabled={loading}
                                                />
                                            </div>
                                        </Col>
                                        <Col lg={6}>
                                            <div className="mb-4">
                                                <label htmlFor="email" className="form-label fs-13">Email</label>
                                                <input 
                                                    name="email" 
                                                    id="email" 
                                                    type="email"
                                                    className="form-control bg-light border-light" 
                                                    placeholder="Your email*"
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                    required
                                                    disabled={loading}
                                                />
                                            </div>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col lg={12}>
                                            <div className="mb-4">
                                                <label htmlFor="subject" className="form-label fs-13">Subject</label>
                                                <input 
                                                    type="text" 
                                                    className="form-control bg-light border-light" 
                                                    id="subject"
                                                    name="subject" 
                                                    placeholder="Your Subject.."
                                                    value={formData.subject}
                                                    onChange={handleChange}
                                                    required
                                                    disabled={loading}
                                                />
                                            </div>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col lg={12}>
                                            <div className="mb-3">
                                                <label htmlFor="message" className="form-label fs-13">Message</label>
                                                <textarea 
                                                    name="message" 
                                                    id="message" 
                                                    rows="3"
                                                    className="form-control bg-light border-light"
                                                    placeholder="Your message..."
                                                    value={formData.message}
                                                    onChange={handleChange}
                                                    required
                                                    disabled={loading}
                                                ></textarea>
                                            </div>
                                        </Col>
                                    </Row>
                                    {/* Honeypot field - hidden from humans, bots will fill it */}
                                    <div style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true">
                                        <label htmlFor="website">Website</label>
                                        <input
                                            type="text"
                                            name="website"
                                            id="website"
                                            value={honeypot}
                                            onChange={(e) => setHoneypot(e.target.value)}
                                            tabIndex="-1"
                                            autoComplete="off"
                                        />
                                    </div>
                                    
                                    <Row>
                                        <Col lg={12} className="text-end">
                                            <button 
                                                type="submit" 
                                                className="btn btn-primary"
                                                disabled={loading}
                                            >
                                                {loading ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                        Sending...
                                                    </>
                                                ) : (
                                                    'Send Message'
                                                )}
                                            </button>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col lg={12}>
                                            <p className="text-muted small mt-3">
                                                This site is protected by reCAPTCHA and the Google{' '}
                                                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> and{' '}
                                                <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a> apply.
                                            </p>
                                        </Col>
                                    </Row>
                                </Form>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>
        </React.Fragment>
    );
};

export default ContactForm;
